import React, {ReactElement} from "react";
import {WorkbenchContainer, WorkbenchTogglesState} from "./WorkbenchContainer";
import {WorkbenchCanvas} from "./WorkbenchCanvas";
import {
  CanvasElement,
  canvasElementsToLabelerTaskLabels,
  CommentElement,
  labelerTaskLabelsToCanvasElements,
  ShapeCanvasElement,
  WORKBENCH_TOOL_KTM,
  WorkbenchToggle,
  WorkbenchTool
} from "./types";
import {
  LabelerSegmentationType,
  LabelerTaskCase,
  LabelerTaskCaseLabel,
  LabelerTaskCategory
} from "@/fyneai-shared/types";
import {AnswerClassPopup} from "./AnswerClassPopup";
import {CommentInputPopup} from "./CommentInputPopup";
import {BaseFragment, BaseFragmentProps, BaseFragmentState} from "@/shared/BaseFragment";

export type WorkbenchCaseFragmentProps = BaseFragmentProps & {
  case: LabelerTaskCase;
}

type WorkbenchCaseFragmentState = BaseFragmentState & {
  selectedTool?: WorkbenchTool;
  elements: CanvasElement[];
  selectedIds: string[];
  brightness: number;
  contrast: number;
  saturation: number;
  rotation: number;
  togglesState: WorkbenchTogglesState;
  answerClassPopup?: {
    elementId: string;
    position: { x: number, y: number };
  };
  comments: CommentElement[];
  commentPopup?: {
    pageX: number;
    pageY: number;
    screenPos: { x: number, y: number };
  };
}

export class WorkbenchCaseFragment extends BaseFragment<WorkbenchCaseFragmentProps, WorkbenchCaseFragmentState> {

  static defaultTool(case_: LabelerTaskCase): WorkbenchTool {
    const task = case_.getTask();
    if (task.category === LabelerTaskCategory.SEGMENTATION) {
      switch (task.segmentationTypes[0]) {
        case LabelerSegmentationType.POINT:
          return WorkbenchTool.POINT;
        case LabelerSegmentationType.ELLIPSE:
          return WorkbenchTool.ELLIPSE;
        case LabelerSegmentationType.RECTANGLE:
          return WorkbenchTool.RECTANGLE;
        case LabelerSegmentationType.POLYGON:
          return WorkbenchTool.POLYGON;
      }
    }
    return null;
  }

  protected onCreateState(props?: WorkbenchCaseFragmentProps): WorkbenchCaseFragmentState {
    return {
      ...super.onCreateState(props),
      selectedTool: WorkbenchCaseFragment.defaultTool(props.case),
      elements: [
        ...labelerTaskLabelsToCanvasElements(props.case.labels),
      ],
      comments: [],
      selectedIds: [],
      brightness: 128,
      contrast: 128,
      saturation: 128,
      rotation: 0,
      togglesState: {
        [WorkbenchToggle.INVERT]: false,
        [WorkbenchToggle.ANNOTATIONS]: true,
        [WorkbenchToggle.LABELS]: true,
        [WorkbenchToggle.POINT_SNAPPING]: false,
        [WorkbenchToggle.CROSSHAIR]: false,
      },
    };
  }

  getTaskCaseLabels(): LabelerTaskCaseLabel[] {
    return canvasElementsToLabelerTaskLabels(this.state.elements);
  }

  protected renderContainerContent(): React.ReactElement | null {
    return <div style={{width: "100dvw", flexGrow: 1}}>
      <WorkbenchContainer
        selectedTool={this.state.selectedTool}
        setSelectedTool={selectedTool => this.setState({selectedTool})}
        brightness={this.state.brightness}
        onBrightnessChange={v => this.setState({brightness: v})}
        contrast={this.state.contrast}
        onContrastChange={v => this.setState({contrast: v})}
        saturation={this.state.saturation}
        onSaturationChange={v => this.setState({saturation: v})}
        rotation={this.state.rotation}
        onRotationChange={v => this.setState({rotation: v})}
        togglesState={this.state.togglesState}
        onToggleStateChange={(key, value) => this.setState(prev => ({
          togglesState: {
            ...prev.togglesState,
            [key]: value
          }
        }))}
        case_={this.props.case}
        elements={this.state.elements.filter(el => el.type === "shape") as ShapeCanvasElement[]}
        onSelectElement={id => this.setState({selectedIds: [id]})}
        onDeleteElement={id => this.setState(prev => ({
          elements: prev.elements.filter(el => el.id !== id),
          selectedIds: prev.selectedIds.filter(sid => sid !== id),
        }))}
        comments={this.state.comments}
        onAddComment={comment => this.setState(prev => ({comments: [...prev.comments, comment]}))}
        onDeleteComment={id => this.setState(prev => ({comments: prev.comments.filter(c => c.id !== id)}))}
        onSelectComment={id => {
        }}
      >
        {this.renderEditorTab()}
      </WorkbenchContainer>
    </div>;
  }

  private renderEditorTab(): ReactElement {
    const {selectedTool, elements, selectedIds} = this.state;
    const selectedElement = selectedIds.length === 1 ? elements.find(el => el.id === selectedIds[0]) || null : null;
    return (
      <div style={{flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden"}}>
        <WorkbenchCanvas
          elements={elements}
          selectedIds={selectedIds}
          selectedTool={WORKBENCH_TOOL_KTM.findMetadata(selectedTool)?.shape}
          panning={selectedTool === WorkbenchTool.PAN}
          ruler={selectedTool === WorkbenchTool.RULER}
          invert={this.state.togglesState[WorkbenchToggle.INVERT]}
          crosshair={this.state.togglesState[WorkbenchToggle.CROSSHAIR]}
          imageUrl={this.props.case.asset.fileUrl}
          brightness={this.state.brightness}
          contrast={this.state.contrast}
          saturation={this.state.saturation}
          rotation={this.state.rotation}
          onSelect={ids => this.setState({selectedIds: ids})}
          onElementUpdate={(id, x, y, width, height, rotation) => {
            this.setState(prev => ({
              elements: prev.elements.map(el =>
                el.id === id ? {...el, x, y, width, height, rotation} : el
              ),
            }));
          }}
          comments={this.state.comments}
          commenting={selectedTool === WorkbenchTool.COMMENT}
          onCommentClick={(px, py, screenPos) => {
            this.setState({commentPopup: {pageX: px, pageY: py, screenPos}});
          }}
          onCommentSelect={id => {
          }}
          onElementAdd={(element: ShapeCanvasElement, screenPos: { x: number, y: number }) => {
            const task = this.props.case.getTask();
            this.setState(prev => ({
              elements: [...prev.elements, element],
              selectedIds: [element.id],
              answerClassPopup: task?.answerClasses?.length ? {
                elementId: element.id,
                position: screenPos,
              } : undefined,
            }));
          }}
        />
        {this.state.answerClassPopup && this.props.case.getTask()?.answerClasses && (
          <AnswerClassPopup
            answerClasses={this.props.case.getTask()?.answerClasses}
            position={this.state.answerClassPopup.position}
            onSelect={answerClass => {
              const elementId = this.state.answerClassPopup?.elementId;
              this.setState(prev => ({
                elements: prev.elements.map(el =>
                  el.id === elementId ? {...(el as ShapeCanvasElement), answerClass} : el
                ),
                answerClassPopup: undefined,
              }));
            }}
            onClose={() => this.setState({answerClassPopup: undefined})}
          />
        )}
        {this.state.commentPopup && (
          <CommentInputPopup
            position={this.state.commentPopup.screenPos}
            onSubmit={text => {
              const {pageX, pageY} = this.state.commentPopup;
              this.setState(prev => ({
                comments: [...prev.comments, {
                  id: `comment_${Date.now()}`,
                  x: pageX,
                  y: pageY,
                  text,
                  timestamp: Date.now(),
                }],
                commentPopup: undefined,
              }));
            }}
            onClose={() => this.setState({commentPopup: undefined})}
          />
        )}
      </div>
    );
  }
}
